// Ubicación en el repo: src/lib/professionalSignatures.js
// Firma digital del profesional (imagen PNG/JPEG) — tabla
// `professionals.signature_path` + bucket privado `signatures` de Supabase
// Storage. Las dos cosas ya existían desde el diseño inicial del esquema
// (migración 0001 columna, 0004 bucket + políticas de storage.objects,
// convención de ruta `{professional_id}/{filename}`, dueño o Admin) — solo
// faltaba esta capa de la app, igual que pasó con branchLogos.js
// (DECISIONS.md #39). Mismo patrón que branchLogos.js: comprimir liviano,
// preservar PNG (para no perder transparencia de una firma escaneada con
// fondo transparente), borrar el archivo viejo al reemplazar.
import { supabase } from './supabaseClient';
import { compressImageIfNeeded } from './imageCompression';

const BUCKET = 'signatures';
// Mismo límite fijado en el bucket (migración 0020) — se valida acá antes
// de subir para dar un mensaje claro en vez del error genérico de Storage.
const MAX_FILE_SIZE_BYTES = 1024 * 1024; // 1 MB — una firma es una imagen muy chica

function signatureCompressOpts() {
  return { maxDimension: 500, jpegQuality: 0.85, skipUnderBytes: 150 * 1024, preserveType: true };
}

// Sube (o reemplaza) la firma de un profesional y actualiza `signature_path`.
// `professionalId` debe existir ya en la tabla (la ruta de Storage usa
// `{professional_id}/...`, ver policy `signatures_storage_all`).
export async function uploadProfessionalSignature(professionalId, file, previousSignaturePath) {
  const toUpload = await compressImageIfNeeded(file, signatureCompressOpts());

  if (toUpload.size > MAX_FILE_SIZE_BYTES) {
    const mb = (toUpload.size / (1024 * 1024)).toFixed(1);
    throw new Error(`La imagen pesa ${mb} MB — el máximo permitido es 1 MB.`);
  }
  const safeName = toUpload.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  const path = `${professionalId}/${crypto.randomUUID()}-${safeName}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, toUpload, { contentType: toUpload.type || undefined });
  if (uploadError) throw uploadError;

  const { error: updateError } = await supabase
    .from('professionals')
    .update({ signature_path: path })
    .eq('id', professionalId);
  if (updateError) {
    // No dejar el archivo huérfano en Storage si no se pudo guardar la referencia.
    await supabase.storage.from(BUCKET).remove([path]);
    throw updateError;
  }

  // Recién ahora que la fila apunta al archivo nuevo se borra el anterior
  // (si había uno) — la firma no es dato clínico del paciente, no aplica la
  // política de nunca borrar (DECISIONS.md #2), igual que un logo.
  if (previousSignaturePath) {
    await supabase.storage.from(BUCKET).remove([previousSignaturePath]);
  }
  return path;
}

export async function removeProfessionalSignature(professionalId, signaturePath) {
  const { error } = await supabase
    .from('professionals')
    .update({ signature_path: null })
    .eq('id', professionalId);
  if (error) throw error;
  if (signaturePath) {
    await supabase.storage.from(BUCKET).remove([signaturePath]);
  }
}

// URL firmada de corta duración (el bucket es privado) — usada tanto para
// la vista previa en el formulario de Profesionales como para los imprimibles.
export async function getSignedSignatureUrl(signaturePath) {
  if (!signaturePath) return null;
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(signaturePath, 60);
  if (error) throw error;
  return data.signedUrl;
}

// Atajo para los imprimibles (examReport.jsx/prescriptionReport.jsx):
// `exam.professionals`/`rx.professionals` ya trae `signature_path` embebido
// (ver EXAM_LIST_SELECT/PRESCRIPTION_LIST_SELECT). Si algo falla (sin firma
// cargada, archivo borrado, etc.) devuelve null en vez de romper la
// impresión: la firma-imagen es un plus visual, el documento se sigue
// pudiendo firmar a mano sobre la línea si no hay una cargada.
export async function getProfessionalSignatureUrl(professional) {
  const path = professional?.signature_path;
  if (!path) return null;
  try {
    return await getSignedSignatureUrl(path);
  } catch {
    return null;
  }
}
